import { FileText, FolderOpen, Keyboard, X } from "lucide-react";
import { useT } from "@magic-md/i18n/react";
import { useMemo } from "react";
import { getBuildInfo } from "../lib/build-info";
import { useEditorStore } from "../store/editor-store";
import { useRecentStore, type RecentItem } from "../store/recent-store";

const MAX_VISIBLE_RECENT_WORKSPACES = 5;

function isWorkspaceHistoryItem(item: RecentItem): boolean {
    return (
        item.type === "workspace" ||
        item.type === "folder" ||
        item.path.toLowerCase().endsWith(".mdspace")
    );
}

function isWorkspaceFile(item: RecentItem): boolean {
    return item.type === "workspace" || item.path.toLowerCase().endsWith(".mdspace");
}

function compactRecentWorkspacePath(item: RecentItem): string {
    const path = item.path;
    const normalized = path.replace(/\\/g, "/");
    const segments = normalized.split("/").filter(Boolean);
    const workspaceFile = isWorkspaceFile(item);
    const visibleSegments = workspaceFile ? 2 : 3;

    if (segments.length <= visibleSegments + 1) return normalized;

    return `.../${segments.slice(-visibleSegments).join("/")}`;
}

export function WelcomeScreen() {
    const t = useT("editor");
    const addTab = useEditorStore((s) => s.addTab);
    const openFile = useEditorStore((s) => s.openFile);
    const openFolderFromPath = useEditorStore((s) => s.openFolderFromPath);
    const loadWorkspaceFromPath = useEditorStore((s) => s.loadWorkspaceFromPath);
    const recentItems = useRecentStore((s) => s.items);
    const removeRecentItem = useRecentStore((s) => s.removeItem);
    const recentWorkspaces = useMemo(
        () =>
            recentItems
                .filter(isWorkspaceHistoryItem)
                .slice(0, MAX_VISIBLE_RECENT_WORKSPACES),
        [recentItems],
    );
    const info = getBuildInfo();

    const openRecentWorkspace = (item: RecentItem) => {
        if (isWorkspaceFile(item)) {
            void loadWorkspaceFromPath(item.path);
            return;
        }
        void openFolderFromPath(item.path);
    };

    return (
        <div className="flex flex-1 items-center justify-center min-h-0 min-w-0 bg-background">
            <div className="flex flex-col items-center gap-6 -mt-8 px-6">
                {/* Logo / brand mark */}
                <div className="flex flex-col items-center gap-2">
                    <img
                        src="/app-icon.png"
                        alt="Magic MD"
                        className="w-14 h-14 rounded-2xl"
                    />
                    <h2 className="text-lg font-semibold text-foreground tracking-tight">
                        Magic MD
                    </h2>
                    <p className="text-sm text-muted-foreground">
                        {t("welcome.subtitle")}
                    </p>
                </div>

                {/* Action buttons */}
                <div className="flex flex-col gap-2 w-56">
                    <button
                        type="button"
                        onClick={() => addTab()}
                        className="flex items-center gap-3 w-full px-4 py-2.5 rounded-lg border border-border bg-card hover:bg-muted/60 transition-colors text-sm text-foreground"
                    >
                        <FileText size={16} className="text-muted-foreground shrink-0" />
                        <span className="flex-1 text-left">{t("command.file.new")}</span>
                        <kbd className="text-[10px] text-muted-foreground/60 font-mono">⌘N</kbd>
                    </button>
                    <button
                        type="button"
                        onClick={() => openFile()}
                        className="flex items-center gap-3 w-full px-4 py-2.5 rounded-lg border border-border bg-card hover:bg-muted/60 transition-colors text-sm text-foreground"
                    >
                        <FolderOpen size={16} className="text-muted-foreground shrink-0" />
                        <span className="flex-1 text-left">{t("command.file.open")}</span>
                        <kbd className="text-[10px] text-muted-foreground/60 font-mono">⌘O</kbd>
                    </button>
                </div>

                {recentWorkspaces.length > 0 ? (
                    <div className="flex w-56 flex-col gap-1.5">
                        <div className="px-1 text-xs font-medium text-muted-foreground">
                            {t("welcome.recentWorkspaces")}
                        </div>
                        <div className="flex flex-col gap-0.5">
                            {recentWorkspaces.map((item) => {
                                const workspaceFile = isWorkspaceFile(item);
                                const Icon = workspaceFile ? FileText : FolderOpen;
                                const displayPath = compactRecentWorkspacePath(item);
                                return (
                                    <div
                                        key={`${item.type}:${item.path}`}
                                        className="group flex items-center"
                                    >
                                        <button
                                            type="button"
                                            title={item.path}
                                            onClick={() => openRecentWorkspace(item)}
                                            className="flex min-w-0 flex-1 items-center gap-2 rounded-md px-1.5 py-1.5 text-left text-xs text-foreground transition-colors hover:bg-muted/60"
                                        >
                                            <Icon
                                                size={14}
                                                className="shrink-0 text-muted-foreground"
                                            />
                                            <span className="min-w-0 flex-1 truncate">
                                                {displayPath}
                                            </span>
                                        </button>
                                        <button
                                            type="button"
                                            title={t("welcome.removeRecent")}
                                            aria-label={t("welcome.removeRecentNamed", {
                                                name: item.name,
                                            })}
                                            onClick={() => removeRecentItem(item.path)}
                                            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground/50 transition-colors hover:bg-muted hover:text-foreground focus:bg-muted focus:text-foreground"
                                        >
                                            <X size={14} />
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ) : null}

                {/* Keyboard hint */}
                <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground/50">
                    <Keyboard size={12} />
                    <span>{t("welcome.commandPaletteHint")}</span>
                </div>

                {/* Build info */}
                <div className="text-[10px] text-muted-foreground/40 text-center font-mono leading-relaxed select-text">
                    <span>v{info.version}</span>
                    {info.gitHash ? <span> · {info.gitHash}</span> : null}
                    <br />
                    <span>{info.buildTime.replace("T", " ").replace(/\.\d+Z$/, " UTC")}</span>
                </div>
            </div>
        </div>
    );
}
